import {
  createModule,
  action,
  mutation,
} from 'vuex-class-component';
import { RepositoryFactory } from '@/repository/factory/repositoryFactory';
import {
  CreateTaskParam,
  FindOneTaskResponse,
  FindTaskCommentsQuery,
  FindTaskCommentsResponse,
  FindTasksResponse,
  FindTasksQuery,
  FindOneTaskQuery,
  UpdateTaskParam,
  UpdateModeTaskQuery,
  UpdateTaskCustomAttributesParam,
  CreateTaskCommentQuery,
  MinimizedTask,
} from '@/@types/Task';

const VuexModule = createModule({
  namespaced: 'theoryApiTasksStore',
  strict: false,
});

export class TasksStore extends VuexModule {
  private readonly tasksRepository = RepositoryFactory.theoryApi.tasksRepository();
  private readonly taskCommentAttachmentFilesRepository = RepositoryFactory.theoryApi.taskCommentAttachmentFilesRepository();
  private task: FindOneTaskResponse | null = null;
  private tasks: FindTasksResponse | null = null;
  private taskComments: FindTaskCommentsResponse | null = null;
  /** 最小化されたタスクのリスト */
  private minimizedTaskList: MinimizedTask[] = [];

  @mutation
  private setTask(payload: FindOneTaskResponse | null) {
    this.task = payload;
  }

  @mutation
  private setTasks(payload: FindTasksResponse | null) {
    this.tasks = payload;
  }

  @mutation
  private setTaskComments(payload: FindTaskCommentsResponse | null) {
    this.taskComments = payload;
  }

  /** タスクをピン留めする */
  @mutation
  private setMinimizedTaskList(payload: MinimizedTask) {
    this.minimizedTaskList.unshift(payload);
  }

  /** タスクのピン止め解除 */
  @mutation
  private removeFromMinimizedTaskList(payload: MinimizedTask) {
    this.minimizedTaskList.forEach((task, index) => {
      if (task.taskId === payload.taskId) {
        this.minimizedTaskList.splice(index, 1);
        return;
      }
    });
  }

  /**全最小化タスク削除 */
  @mutation
  private deleteFromMinimizedTaskList() {
    this.minimizedTaskList.splice(0);
  }

  /** 課題登録 */
  @action
  async createTask(param: CreateTaskParam) {
    return await this.tasksRepository.createTask(param);
  }

  /** 課題詳細取得 */
  @action
  async fetchTask(params: { taskId: number, query: FindOneTaskQuery }) {
    const results = await this.tasksRepository.findOneTask(params.taskId, params.query);
    this.setTask(results);
  }

  /** 課題一覧取得 */
  @action
  async fetchTasks(query: FindTasksQuery) {
    const results = await this.tasksRepository.findTasks(query);
    this.setTasks(results);
  }

  /** 課題更新 */
  @action
  async updateTask(params: {
    taskId: number,
    param: UpdateTaskParam,
    query: UpdateModeTaskQuery,
  }) {
    return await this.tasksRepository.updateTask(
      params.taskId,
      params.param,
      params.query,
    );
  }

  /** 課題カスタム属性更新 */
  @action
  async updateTaskCustomAttributes(params: {
    taskId: number,
    param: UpdateTaskCustomAttributesParam,
    query: UpdateModeTaskQuery,
  }) {
    return await this.tasksRepository.updateTaskCustomAttributes(
      params.taskId,
      params.param,
      params.query,
    );
  }

  /** 課題コメント登録 */
  @action
  async createTaskComment(params: {
    taskId: number,
    param: FormData,
    query: CreateTaskCommentQuery,
  }) {
    return await this.tasksRepository.createTaskComment(
      params.taskId,
      params.param,
      params.query,
    );
  }

  /** 課題コメント一覧取得 */
  @action
  async fetchTaskComments(params: {
    taskId: number,
    query: FindTaskCommentsQuery,
  }) {
    const results = await this.tasksRepository.findTaskComments(params.taskId, params.query);
    this.setTaskComments(results);
  }

  /**課題コメント添付ファイル削除 */
  @action
  async deleteTaskCommentAttachmentFile(params: {
    taskCommentAttachmentId: number,
    projectId: number,
  }) {
    await this.taskCommentAttachmentFilesRepository.deleteTaskCommentAttachmentFile(
      params.taskCommentAttachmentId,
      params.projectId,
    );
  }


  /** タスクをピン留めする */
  @action
  async fetchMinimizedTask(task: MinimizedTask) {
    this.setMinimizedTaskList(task);
  }

  /** タスクのピン止め解除 */
  @action
  async removeMinimizedTask(task: MinimizedTask) {
    this.removeFromMinimizedTaskList(task);
  }

  @action
  async deleteTask() {
    this.setTask(null);
  }

  @action
  async deleteTaskComments() {
    this.setTaskComments(null);
  }

  @action
  async deleteTasks() {
    this.setTasks(null);
  }

  get getTask() {
    return this.task;
  }

  get getTasks() {
    return this.tasks;
  }

  get getTaskComments() {
    return this.taskComments;
  }

  get getMinimizedTaskList() {
    return this.minimizedTaskList;
  }
}
